import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RiUserLine, RiLockPasswordLine, RiLogoutBoxLine } from "react-icons/ri";
import { logout } from "../../Redux/Slices/authSlice";
import { post } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import { logger } from "../../utils/logger";
import { applyRememberMeStorageOnSessionEnd } from "../../utils/rememberMeStorage";

/** Matches ApiBase refresh / Bearer parsing — refresh token for POST /auth/logout body. */
function readRefreshTokenFromAuthStorage() {
  try {
    const raw = localStorage.getItem("authToken");
    if (!raw) return "";
    const tokenData = JSON.parse(raw);
    const nested =
      tokenData?.data != null &&
      typeof tokenData.data === "object" &&
      !Array.isArray(tokenData.data)
        ? tokenData.data
        : null;
    const rt =
      tokenData?.refreshToken ??
      tokenData?.refresh_token ??
      nested?.refreshToken ??
      nested?.refresh_token ??
      "";
    if (rt == null) return "";
    const s = typeof rt === "string" ? rt.trim() : String(rt).trim();
    return s;
  } catch {
    return "";
  }
}

const PmHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get user's initial character
  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "D"; // Fallback to match "David Adress"
  };

  // Get user's display name
  const getUserName = () => {
    if (user?.name) return user.name;
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) return user.firstName;
    if (user?.email) return user.email;
    return "David Adress"; // Fallback
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle logout
  const handleLogout = async () => {
    const refreshToken = readRefreshTokenFromAuthStorage();
    try {
      if (refreshToken) {
        await post(API_ENDPOINTS.AUTH.LOGOUT, { refreshToken });
      }
    } catch (error) {
      logger.error("Logout error:", error);
    } finally {
      dispatch(logout());
      localStorage.removeItem("authToken");
      applyRememberMeStorageOnSessionEnd();
      navigate("/login");
    }
  };

  // Handle Edit Profile
  const handleEditProfile = () => {
    setDropdownOpen(false);
    navigate("/profile/edit");
  };

  // Handle Change Password
  const handleChangePassword = () => {
    setDropdownOpen(false);
    navigate("/change-password");
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40 relative">
      <div className="flex items-center justify-end h-16 px-4 sm:px-6 lg:px-8">
        {/* Right Section - User Profile with Dropdown */}
        <div className="flex items-center space-x-3 relative" ref={dropdownRef}>
          {/* User Name (hidden on mobile) */}
          <div className="hidden sm:flex items-center space-x-2">
            <Typography variant="text2" component="span" className="font-medium text-gray-700">
              {getUserName()}
            </Typography>
          </div>

          {/* Avatar with Dropdown */}
          <div className="relative">
            <Button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="!p-0 hover:!opacity-80 !transition-opacity focus:!outline-none focus:!ring-0 focus:!ring-offset-0 !border-0 !bg-transparent !shadow-none !font-normal !rounded-none"
              aria-label="User menu"
            >
              <div className="avatar-circle">{getUserInitial()}</div>
            </Button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <Button
                  type="button"
                  onClick={handleEditProfile}
                  className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                >
                  <RiUserLine className="text-lg text-gray-500" />
                  <Typography variant="span1">Edit Profile</Typography>
                </Button>
                <Button
                  type="button"
                  onClick={handleChangePassword}
                  className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                >
                  <RiLockPasswordLine className="text-lg text-gray-500" />
                  <Typography variant="span1">Change Password</Typography>
                </Button>
                <div className="border-t border-gray-200 my-1"></div>
                <Button
                  type="button"
                  onClick={handleLogout}
                  className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-red-600 hover:!bg-red-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                >
                  <RiLogoutBoxLine className="text-lg text-red-500" />
                  <Typography variant="span1">Logout</Typography>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default PmHeader;
